import { cardsData, chartData } from "./../../../shared/models/dashboard.model";
import { ApiService } from "./../../../shared/services/api/api.service";
import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnInit,
  WritableSignal,
  signal,
  computed,
  effect,
  inject,
} from "@angular/core";
import { EmptyComponent } from "../../../shared/components/empty/empty.component";
import {
  OptionSwitchComponent,
  OptionSwitchItem,
} from "../../../shared/components/option-switch/option-switch.component";
import { FullCalendarModule } from "@fullcalendar/angular";
import {
  CalendarOptions,
  DatesSetArg,
  EventInput,
} from "@fullcalendar/core/index.js";
import dayGridPlugin from "@fullcalendar/daygrid";
import { RevenueChartLineService } from "../../../shared/services/revenue-line-chart.service";
import { AuthService } from "../../../shared/services/auth.service";
import { CommonModule } from "@angular/common";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import {
  ApiResponse,
  Appointment,
} from "../../../shared/models/appointment.model";
import { RouterModule } from "@angular/router";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import dayjs from "dayjs";
import { environment } from "../../../../environments/environment";
import { BusinessSalonContextService } from "../../../shared/services/api/business-salon-context.service";

@Component({
  selector: "business-overview",
  imports: [
    EmptyComponent,
    OptionSwitchComponent,
    FullCalendarModule,
    CommonModule,
    RouterModule,
    LoaderComponent,
  ],
  templateUrl: "./overview.component.html",
  styleUrl: "./overview.component.scss",
  standalone: true,
})
export class BusinessOverviewComponent implements AfterViewInit, OnInit {
  @ViewChild("chartCanvas", { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  assetUrl = environment.assetBaseUrl;

  private revenueChartService = inject(RevenueChartLineService);
  private salonContext = inject(BusinessSalonContextService);
  private chartInitialized = false;

  selectedPeriod = "yearly";
  visibleRangeText = "";
  error: string | null = null;
  totalItems = 0;

  private analyticsLoading = false;
  private appointmentsLoading = false;

  get isLoading(): boolean {
    return this.analyticsLoading || this.appointmentsLoading;
  }

  cardsData: cardsData = {} as cardsData;
  ChartData: WritableSignal<chartData> = signal<chartData>({} as chartData);
  appointments: WritableSignal<Appointment[]> = signal<Appointment[]>([]);

  userName = "";

  periodOptions: OptionSwitchItem[] = [
    { label: "Yearly", value: "yearly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Monthly", value: "monthly" },
    { label: "Weekly", value: "weekly" },
    { label: "Daily", value: "daily" },
  ];

  filteredAppointments = computed(() => {
    const filteredAppts = this.getFilteredAppointments(this.appointments());
    return filteredAppts.slice(0, 5);
  });

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin],
    initialView: "dayGridMonth",
    headerToolbar: {
      left: "title",
      right: "",
    },
    events: [],
    eventContent: (arg) => {
      return {
        html: `<span style="
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${arg.event.backgroundColor || "#111827"};
          margin-right: 4px;
          margin-top: 2px;
        "></span>`,
      };
    },
    datesSet: this.onDatesSet.bind(this),
    dayMaxEventRows: 4,
    height: "auto",
  };

  constructor(
    private apiServices: ApiService,
    private authService: AuthService,
  ) {
    effect(() => {
      const events = this.appointments();

      this.calendarOptions = {
        ...this.calendarOptions,
        events: this.generateCalendarEvents(events),
      };
    });

    effect(() => {
      const data = this.ChartData();

      if (!this.chartInitialized) return;

      this.revenueChartService.updateChartData(this.selectedPeriod, data);
    });

    /*
     * Reload the overview whenever the global salon selection changes.
     *
     * BusinessComponent loads the salon list into the shared context.
     * Until that has happened, do not make an unscoped/empty request.
     */
    effect(() => {
      const salons = this.salonContext.salons();
      this.salonContext.selectedSalonIds();

      if (!salons.length) {
        return;
      }

      this.loadAnalyticsData();
      this.loadAppointments();
    });
  }

  ngOnInit(): void {
    this.authService.getUserDetails().subscribe({
      next: (user) => {
        this.userName = user?.full_name || "User";
      },
      error: () => {
        this.userName = "User";
      },
    });

    /*
     * Analytics and appointments are loaded by the salon-context effect.
     * This prevents duplicate requests on initial page load and ensures the
     * first data request already reflects the selected salon scope.
     */
  }

  ngAfterViewInit(): void {
    this.revenueChartService.initChart(this.chartCanvas.nativeElement);
    this.chartInitialized = true;

    this.revenueChartService.updateChartData(
      this.selectedPeriod,
      this.ChartData(),
    );
  }

  get totalRevenue(): number {
    return Number(this.cardsData?.total_revenue || 0);
  }

  get totalAppointments(): number {
    return Number(this.cardsData?.appointments_booked || 0);
  }

  get totalCustomers(): number {
    return Number(this.cardsData?.total_customers || 0);
  }

  get totalStaff(): number {
    return Number(this.cardsData?.total_staff || 0);
  }

  get revenuePerAppointment(): number {
    return this.totalAppointments
      ? this.totalRevenue / this.totalAppointments
      : 0;
  }

  get averageCustomerValue(): number {
    return this.totalCustomers ? this.totalRevenue / this.totalCustomers : 0;
  }

  get revenuePerStaff(): number {
    return this.totalStaff ? this.totalRevenue / this.totalStaff : 0;
  }

  get completedAppointments(): number {
    return this.appointments().filter(
      (appointment) => appointment.status === "completed",
    ).length;
  }

  get pendingAppointments(): number {
    return this.appointments().filter(
      (appointment) => appointment.status === "pending",
    ).length;
  }

  get cancelledAppointments(): number {
    return this.appointments().filter(
      (appointment) => appointment.status === "cancelled",
    ).length;
  }

  get completionRate(): number {
    const total = this.appointments().length;

    if (!total) return 0;

    return Math.round((this.completedAppointments / total) * 100);
  }

  get cancellationRate(): number {
    const total = this.appointments().length;

    if (!total) return 0;

    return Math.round((this.cancelledAppointments / total) * 100);
  }

  get upcomingAppointmentRevenue(): number {
    return this.filteredAppointments().reduce(
      (sum, appointment) => sum + Number(appointment.total_price || 0),
      0,
    );
  }

  loadAnalyticsData(): void {
    this.analyticsLoading = true;

    const url = this.salonContext.withSalonScope(
      API_ENDPOINTS.owner.analytics.overview,
    );

    this.apiServices.request<any>(url, "GET").subscribe({
      next: (response: any) => {
        this.cardsData = response?.cards || ({} as cardsData);
        this.ChartData.set(response?.charts || ({} as chartData));

        if (this.chartInitialized) {
          this.revenueChartService.updateChartData(
            this.selectedPeriod,
            response?.charts || ({} as chartData),
          );
        }

        this.analyticsLoading = false;
      },

      error: (error: any) => {
        console.error("Error loading analytics data:", error);

        /*
         * Clear stale data so changing to a salon scope that has no results
         * does not leave the previous salon's KPIs/chart on screen.
         */
        this.cardsData = {} as cardsData;
        this.ChartData.set({} as chartData);

        this.analyticsLoading = false;
      },
    });
  }

  loadAppointments(): void {
    this.appointmentsLoading = true;
    this.error = null;

    const url = this.salonContext.withSalonScope(
      API_ENDPOINTS.owner.appointments.list,
    );

    this.apiServices.request<ApiResponse>(url, "GET").subscribe({
      next: (response: ApiResponse) => {
        this.appointments.set(response?.results || []);
        this.totalItems = response?.count || 0;
        this.appointmentsLoading = false;
      },

      error: (error: any) => {
        console.error("Error loading appointments:", error);

        /*
         * Do not leave appointments from the previous salon selection visible
         * if the newly selected scope fails or contains no accessible salons.
         */
        this.appointments.set([]);
        this.totalItems = 0;
        this.error = "Failed to load appointments. Please try again.";
        this.appointmentsLoading = false;
      },
    });
  }

  getFilteredAppointments(appointments: Appointment[]): Appointment[] {
    return appointments.filter((appointment: Appointment) => {
      const start = new Date(appointment.start_time);

      if (isNaN(start.getTime())) return false;

      return start > new Date();
    });
  }

  private generateCalendarEvents(eventsArray: Appointment[]): EventInput[] {
    if (!eventsArray.length) return [];

    const statusColors: Record<string, string> = {
      completed: "#22c55e",
      pending: "#9ca3af",
      cancelled: "#f97316",
    };

    const fallbackColors = [
      "#8b5cf6",
      "#38bdf8",
      "#111827",
      "#a855f7",
      "#ec4899",
    ];

    return eventsArray.map((event, index) => {
      const eventDate = new Date(event.start_time);

      return {
        title: event.status,
        start: this.formatDate(eventDate),
        color:
          statusColors[event.status] ||
          fallbackColors[index % fallbackColors.length],
      };
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  formatDateString(dateString: string, format: string): string {
    return dayjs(dateString).format(format || "MMM D, YYYY");
  }

  formatTime(dateString: string): string {
    return dayjs(dateString).format("h:mm A");
  }

  onPeriodChange(selectedPeriod: string): void {
    this.selectedPeriod = selectedPeriod;

    if (!this.chartInitialized) return;

    this.revenueChartService.updateChartData(selectedPeriod, this.ChartData());
  }

  onDatesSet(dateInfo: DatesSetArg): void {
    const start = new Date(dateInfo.start);
    const end = new Date(dateInfo.end);

    const displayEnd = new Date(end);
    displayEnd.setDate(end.getDate() - 1);

    const format = (date: Date) =>
      date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

    const nextValue =
      displayEnd < start
        ? format(start)
        : `${format(start)} – ${format(displayEnd)}`;

    queueMicrotask(() => {
      this.visibleRangeText = nextValue;
    });
  }

  downloadReport(): void {
    const labels = this.revenueChartService.getChartLabels(
      this.selectedPeriod,
      this.ChartData(),
    );

    const data = this.revenueChartService.getChartValues(
      this.selectedPeriod,
      this.ChartData(),
    );

    const BOM = "\uFEFF";
    let csvContent = "Time Period,Revenue ($)\n";

    labels.forEach((label, index) => {
      const revenue = data[index] ?? 0;
      csvContent += `${label},${revenue}\n`;
    });

    const blob = new Blob([BOM, csvContent], {
      type: "text/csv",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const filename = `revenue-report-${this.selectedPeriod}.csv`;

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
